import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, Subscription, timer, of } from 'rxjs';
import { map, tap, switchMap, catchError, shareReplay, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { Message, MessageResponse, PaginatedMessages, User, SendMessageRequest, AvailableUsersResponse } from 'app/models/message.model';
import { AuthenticationService } from './authentication.service';

@Injectable({
  providedIn: 'root'
})
export class MessagesService {
  private baseUrl = `${environment.baseUrl}messages`;
  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();
  private unreadPollingSubscription?: Subscription;
  private readonly unreadPollingInterval = 30000; // 30 seconds
  private unreadCountCache$?: Observable<number>; // Cache para compartir peticiones
  private lastUnreadFetch: number = 0; // Timestamp del último fetch
  private readonly minFetchInterval = 2000; // Mínimo 2 segundos entre fetches

  constructor(
    private http: HttpClient,
    private authService: AuthenticationService
  ) {
    // NO iniciar polling automáticamente
    // El polling se iniciará cuando el componente de mensajes se cargue
    // o cuando MessagesComponent llame explícitamente a initPolling()
  }

  /**
   * Get inbox messages with optional filters
   */
  getInboxMessages(page: number = 1, perPage: number = 10, type?: string, status?: string, sender?: string, subject?: string, content?: string, dateFrom?: string, dateTo?: string, priority?: string): Observable<PaginatedMessages> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('per_page', perPage.toString());

    if (type) {
      params = params.set('type', type);
    }

    if (status) {
      params = params.set('status', status);
    }

    if (sender) {
      params = params.set('sender', sender);
    }

    if (subject) {
      params = params.set('subject', subject);
    }

    if (content) {
      params = params.set('content', content);
    }

    if (dateFrom) {
      params = params.set('date_from', dateFrom);
    }

    if (dateTo) {
      params = params.set('date_to', dateTo);
    }

    if (priority) {
      params = params.set('priority', priority);
    }

    return this.http.get<MessageResponse>(`${this.baseUrl}/inbox`, { params })
      .pipe(
        map(response => response.result),
        tap(() => this.loadUnreadCount())
      );
  }

  /**
   * Get sent messages
   */
  getSentMessages(page: number = 1, perPage: number = 10, type?: string, recipient?: string, subject?: string, content?: string, dateFrom?: string, dateTo?: string, priority?: string): Observable<PaginatedMessages> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('per_page', perPage.toString());

    if (type) {
      params = params.set('type', type);
    }

    if (recipient) {
      params = params.set('recipient', recipient);
    }

    if (subject) {
      params = params.set('subject', subject);
    }

    if (content) {
      params = params.set('content', content);
    }

    if (dateFrom) {
      params = params.set('date_from', dateFrom);
    }

    if (dateTo) {
      params = params.set('date_to', dateTo);
    }

    if (priority) {
      params = params.set('priority', priority);
    }

    return this.http.get<MessageResponse>(`${this.baseUrl}/sent`, { params })
      .pipe(map(response => response.result));
  }

  /**
   * Get a specific message
   */
  getMessage(id: number, isScheduled: boolean = false): Observable<Message> {
    let params = new HttpParams();
    if (isScheduled) {
      params = params.set('is_scheduled', 'true');
    }

    return this.http.get<MessageResponse>(`${this.baseUrl}/${id}`, { params })
      .pipe(
        map(response => response.result),
        tap(() => this.loadUnreadCount())
      );
  }

  /**
   * Send a new message
   */
  sendMessage(messageData: SendMessageRequest): Observable<any> {
    return this.http.post<MessageResponse>(this.baseUrl, messageData)
      .pipe(map(response => response.result));
  }

  /**
   * Mark message as read
   */
  markAsRead(id: number): Observable<any> {
    return this.http.patch<MessageResponse>(`${this.baseUrl}/${id}/read`, {})
      .pipe(
        map(response => response.result),
        tap(() => this.loadUnreadCount())
      );
  }

  /**
   * Mark message as unread
   */
  markAsUnread(id: number): Observable<any> {
    return this.http.patch<MessageResponse>(`${this.baseUrl}/${id}/unread`, {})
      .pipe(
        map(response => response.result),
        tap(() => this.loadUnreadCount())
      );
  }

  /**
   * Archive message
   */
  archiveMessage(id: number): Observable<any> {
    return this.http.patch<MessageResponse>(`${this.baseUrl}/${id}/archive`, {})
      .pipe(map(response => response.result));
  }

  /**
   * Unarchive message
   */
  unarchiveMessage(id: number): Observable<any> {
    return this.http.patch<MessageResponse>(`${this.baseUrl}/${id}/unarchive`, {})
      .pipe(map(response => response.result));
  }

  /**
   * Delete message
   */
  deleteMessage(id: number): Observable<any> {
    return this.http.delete<MessageResponse>(`${this.baseUrl}/${id}`)
      .pipe(map(response => response.result));
  }

  /**
   * Cancel a scheduled message
   */
  cancelScheduledMessage(id: number): Observable<any> {
    return this.http.delete<MessageResponse>(`${this.baseUrl}/scheduled/${id}`)
      .pipe(map(response => response.result));
  }

  /**
   * Get unread messages count with intelligent caching
   */
  getUnreadCount(): Observable<number> {
    const now = Date.now();
    
    // Si hay un fetch en progreso, compartirlo (evita peticiones duplicadas)
    if (this.unreadCountCache$) {
      return this.unreadCountCache$;
    }

    // Si el último fetch fue hace menos de 2 segundos, devolver el valor cacheado
    if (now - this.lastUnreadFetch < this.minFetchInterval) {
      return of(this.unreadCountSubject.value);
    }

    // Crear nueva petición compartida
    this.lastUnreadFetch = now;
    this.unreadCountCache$ = this.http.get<MessageResponse>(`${this.baseUrl}/unread-count`)
      .pipe(
        map(response => response.result.unread_count),
        tap(count => {
          this.unreadCountSubject.next(count);
          // Limpiar caché después de completar
          setTimeout(() => {
            this.unreadCountCache$ = undefined;
          }, 100);
        }),
        shareReplay(1), // Compartir el resultado con múltiples suscriptores
        catchError((error) => {
          // Limpiar caché en caso de error
          this.unreadCountCache$ = undefined;
          throw error;
        })
      );

    return this.unreadCountCache$;
  }

  /**
   * Load unread count and update subject (deprecated - usar directamente getUnreadCount)
   */
  private loadUnreadCount(): void {
    this.refreshUnreadCount();
  }

  /**
   * Refresh unread count and update subject
   */
  refreshUnreadCount(): void {
    this.getUnreadCount().subscribe({
      next: () => {},
      error: () => {}
    });
  }

  /**
   * Initialize polling - debe ser llamado cuando el usuario esté autenticado
   */
  public initPolling(): void {
    // Verificar que el usuario esté autenticado antes de iniciar polling
    if (!this.authService.currentUserValue) {
      console.warn('MessagesService: Cannot start polling - user not authenticated');
      return;
    }

    // Si ya hay un polling activo, no iniciamos otro
    if (this.unreadPollingSubscription) {
      return;
    }
    
    this.startUnreadPolling();
  }

  /**
   * Stop polling - llamar cuando el usuario cierre sesión
   */
  public stopPolling(): void {
    if (this.unreadPollingSubscription) {
      this.unreadPollingSubscription.unsubscribe();
      this.unreadPollingSubscription = undefined;
      console.log('MessagesService: Polling stopped');
    }
    // Limpiar caché también
    this.unreadCountCache$ = undefined;
  }

  /**
   * Start background polling to keep the unread counter up-to-date globally
   */
  private startUnreadPolling(): void {
    // Prevent multiple polling subscriptions
    if (this.unreadPollingSubscription) {
      console.warn('MessagesService: Polling already started, skipping...');
      return;
    }

    // Don't start polling on server-side rendering
    if (typeof window === 'undefined') {
      return;
    }

    // Verificar autenticación antes de iniciar
    if (!this.authService.currentUserValue) {
      console.warn('MessagesService: Cannot start polling - user not authenticated');
      return;
    }

    console.log('MessagesService: Starting unread count polling every 30 seconds');

    this.unreadPollingSubscription = timer(0, this.unreadPollingInterval)
      .pipe(
        switchMap(() => {
          // Verificar autenticación en cada ciclo
          if (!this.authService.currentUserValue) {
            console.warn('MessagesService: User logged out, stopping polling');
            this.stopPolling();
            return of(null);
          }

          // Don't poll when tab is hidden to save resources
          if (typeof document !== 'undefined' && document.hidden) {
            return of(null);
          }

          return this.getUnreadCount().pipe(
            catchError((error) => {
              // Si es un error 401, detener el polling
              if (error.status === 401) {
                console.warn('MessagesService: Unauthorized - stopping polling');
                this.stopPolling();
                return of(null);
              }

              // Only log error once every 5 minutes to avoid console spam
              const now = Date.now();
              const lastErrorTime = (window as any)._lastMessagingErrorLog || 0;
              
              if (now - lastErrorTime > 300000) { // 5 minutes
                console.error('MessagesService: Cannot connect to backend. Make sure Laravel server is running on http://127.0.0.1:8000');
                (window as any)._lastMessagingErrorLog = now;
              }
              
              return of(null);
            })
          );
        })
      )
      .subscribe();
  }

  /**
   * Get available users to send messages to
   */
  getAvailableUsers(search?: string): Observable<AvailableUsersResponse> {
    let params = new HttpParams();
    
    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<MessageResponse>(`${this.baseUrl}/available-users`, { params })
      .pipe(map(response => response.result));
  }

  /**
   * Get priority color class
   */
  getPriorityColor(priority: string): string {
    switch (priority) {
      case 'high':
        return 'text-red-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  }

  /**
   * Get type icon
   */
  getTypeIcon(type: string): string {
    switch (type) {
      case 'note':
        return 'heroicons_outline:document-text';
      case 'message':
      default:
        return 'heroicons_outline:mail';
    }
  }
}
